import { useState } from "react";
import { Activity, Shield, Layers, Radio, Gauge, Maximize2 } from "lucide-react";

const NEEDLE_IMG = "/images/cami_p3_img1.jpeg";

const treatmentHeads = [
  {
    id: "nv",
    series: "NV Series",
    title: "Non-Insulated Vacuum-Assisted",
    color: "#FFB6C1",
    bentoClass: "bento-nv",
    frequency: "2.2 MHz",
    depth: "0.2 – 4.5 mm",
    configs: ["NV64 (8×8)", "NV49 (7×7)", "NV36 (6×6)", "NV9 (3×3)"],
    features: [
      "RF energy along entire needle length",
      "Vacuum tip ensures even depth",
      "29mmHg negative pressure",
      "Inter-needle RF loop type",
    ],
    ideal: "Broad skin texture, acne scarring, improved tone",
    areas: ["Cheek", "Forehead", "Body", "Chin", "Under-eye"],
    icon: Activity,
  },
  {
    id: "is",
    series: "IS Series",
    title: "Insulated Layered",
    color: "#FFB347",
    bentoClass: "bento-is",
    frequency: "700 KHz",
    depth: "0.2 – 4.5 mm",
    configs: ["IS49 (7×7)", "IS25 (5×5)", "IS5 (1×5)"],
    features: [
      "Insulated needles protect epidermis",
      "1–5 depth layer stratification",
      "Minimized downtime & complications",
      "Safe for all skin types including darker tones",
    ],
    ideal: "Deep scars, surface texture, multi-layer targeting",
    areas: ["Cheek", "Chin", "Body", "Forehead", "Periorbital"],
    icon: Shield,
  },
  {
    id: "ic",
    series: "IC Series",
    title: "Common Pole Insulated",
    color: "#E5E4E2",
    bentoClass: "bento-ic",
    frequency: "700 KHz",
    depth: "0.5 – 7.0 mm",
    configs: ["IC40 (4×5+5×4)", "IC24 (3×4+4×3)", "IC12 (2×3+3×2)"],
    features: [
      "Deepest penetration up to 7mm",
      "Common-pole RF loop for even heat",
      "5-layer depth stratification",
      "Insulated needle protection",
    ],
    ideal: "Deep tissue remodeling, body contouring, severe laxity",
    areas: ["Deep facial", "Body", "Submental"],
    icon: Layers,
  },
  {
    id: "hr",
    series: "HR Series",
    title: "Thermage Flat Heads",
    color: "#D4AF37",
    bentoClass: "bento-hr",
    frequency: "Non-invasive RF",
    depth: "Surface level",
    configs: ["HR Large", "HR Medium", "HR Small"],
    features: [
      "Completely non-invasive RF delivery",
      "Surface-level skin tightening",
      "No needles — zero downtime",
      "Ideal for maintenance sessions",
    ],
    ideal: "Skin tightening, collagen stimulation, no-needle option",
    areas: ["Full face", "Neck", "Body"],
    icon: Radio,
  },
];

function HeadDetailPanel({ head }) {
  return (
    <div className="space-y-6" data-testid={`head-detail-${head.id}`}>
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: head.color }}
          />
          <span
            className="text-xs tracking-[0.2em] uppercase"
            style={{ color: head.color, fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
          >
            {head.series}
          </span>
        </div>
        <h3
          className="text-2xl sm:text-3xl font-light text-white"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {head.title}
        </h3>
      </div>

      {/* Quick Specs */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 border border-white/10 bg-white/5">
          <Gauge size={12} style={{ color: head.color }} />
          <span className="text-xs text-white/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {head.frequency}
          </span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 border border-white/10 bg-white/5">
          <Maximize2 size={12} style={{ color: head.color }} />
          <span className="text-xs text-white/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {head.depth}
          </span>
        </div>
      </div>

      {/* Configs */}
      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-white/30 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Needle Configurations
        </p>
        <div className="flex flex-wrap gap-2">
          {head.configs.map((cfg) => (
            <span
              key={cfg}
              className="text-xs px-3 py-1.5 border text-white/70"
              style={{
                borderColor: `${head.color}30`,
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              {cfg}
            </span>
          ))}
        </div>
      </div>

      {/* Features */}
      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-white/30 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Key Features
        </p>
        <ul className="space-y-2">
          {head.features.map((f, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: head.color }} />
              <span className="text-sm text-white/50 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {f}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Ideal For */}
      <div className="p-4 bg-white/[0.03] border border-white/5">
        <p className="text-xs tracking-[0.1em] uppercase text-white/30 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Ideal For
        </p>
        <p className="text-sm text-white/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {head.ideal}
        </p>
      </div>

      {/* Treatment Areas */}
      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-white/30 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Treatment Areas
        </p>
        <div className="flex flex-wrap gap-2">
          {head.areas.map((area) => (
            <span
              key={area}
              className="text-xs px-3 py-1 bg-white/5 text-white/40"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {area}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TreatmentHeads() {
  const [activeHead, setActiveHead] = useState("nv");
  const currentHead = treatmentHeads.find((h) => h.id === activeHead);

  return (
    <section
      id="heads"
      data-testid="treatment-heads-section"
      className="py-24 lg:py-32 px-6 lg:px-12"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="reveal mb-16">
          <p
            className="text-xs tracking-[0.3em] uppercase mb-4"
            style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
          >
            Interchangeable Systems
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-white leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Four Series.<br />
            <span className="text-white/40">Infinite Possibilities.</span>
          </h2>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Left - Selector Cards (Bento Grid) */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {treatmentHeads.map((head) => {
              const Icon = head.icon;
              const isActive = activeHead === head.id;
              return (
                <button
                  key={head.id}
                  onClick={() => setActiveHead(head.id)}
                  className={`bento-card ${head.bentoClass} p-6 text-left cursor-pointer ${
                    isActive ? "border-white/20" : ""
                  }`}
                  style={isActive ? { borderColor: `${head.color}40` } : {}}
                  data-testid={`head-selector-${head.id}`}
                >
                  <Icon
                    size={20}
                    className="mb-4"
                    style={{ color: isActive ? head.color : "rgba(255,255,255,0.3)" }}
                  />
                  <div
                    className="text-lg font-light text-white mb-1"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {head.series}
                  </div>
                  <p className="text-xs text-white/30 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {head.title}
                  </p>
                  <div className="mt-4 flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: head.color, opacity: isActive ? 1 : 0.3 }}
                    />
                    <span className="text-[10px] text-white/20 uppercase tracking-widest" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {head.frequency}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Center - Needle Image */}
          <div className="lg:col-span-3 hidden lg:flex items-center justify-center reveal">
            <div className="relative overflow-hidden rounded-sm border border-white/10">
              <img
                src={NEEDLE_IMG}
                alt="Cami X Treatment Heads Overview"
                className="relative w-full h-auto object-contain"
                data-testid="needle-image"
              />
            </div>
          </div>

          {/* Right - Detail Panel */}
          <div className="lg:col-span-4 glass-card p-8 reveal">
            {currentHead && <HeadDetailPanel head={currentHead} />}
          </div>
        </div>
      </div>
    </section>
  );
}
